import { Link } from 'react-router-dom';
import PageShell, { PageTitle } from '../components/PageShell';
import CallButton from '../components/CallButton';
import HelpBox from '../components/HelpBox';
import ArticleList from '../components/ArticleList';
import FaqAccordion from '../components/FaqAccordion';
import { articlePath, articlesIn } from '../constants/articles';
import { IMAGES } from '../constants/images';

const FEATURES = [
  ['FamilySync', 'Links your desktop tree to one Ancestry tree so additions and corrections can travel in both directions.'],
  ['TreeVault Cloud Services', 'Keeps an online copy of your trees, which is useful if a computer fails or you buy a new one.'],
  ['Color Coding', 'Lets you paint each ancestral line a different color so large trees are easier to read.'],
  ['Photo Tools', 'Repairs faded or damaged pictures and attaches them to the right person, event, or source.'],
  ['FamilySearch Integration', 'Compares your people with the free FamilySearch Family Tree and copies over useful facts and sources.'],
  ['Charts & Reports', 'Produces pedigree, descendant, and fan charts, detailed reports, and printable family books.'],
  ['Web Search & Hints', 'Looks up online records and flags ones that may belong to people in your tree.'],
  ['Media Management', 'Catalogs photos, documents, and recordings and helps you track down any that go missing.'],
];

const SHOWCASE = [
  {
    img: IMAGES.features.color,
    title: 'See Every Branch at Once',
    text: 'Color coding lets you mark a person and all their ancestors in one go, so your mother’s side and father’s side never blur together.',
    link: 'color-coding-features-ftm-2019',
  },
  {
    img: IMAGES.features.photo,
    title: 'Rescue Faded Photos',
    text: 'Built-in adjustments bring back detail in old prints while the untouched original stays in your media library.',
  },
  {
    img: IMAGES.features.charts,
    title: 'Print a Family Book',
    text: 'The Family Book Creator plugin gathers charts, stories, and pictures into a keepsake you can print or give as a gift.',
    link: 'family-book-creator-ftm-2019',
  },
];

const COMMON_ISSUES = [
  ['Program will not start or keeps crashing', 'troubleshooting-family-tree-maker-2019'],
  ['Photos showing as missing', 'fixing-broken-media-links-ftm-2019'],
  ['Setting up on a new computer', 'transfer-ftm-2019-to-new-computer'],
  ['Using one tree on two computers', 'family-tree-maker-multiple-computers'],
  ['Preventing a damaged tree file', 'avoid-ftm-2019-data-corruption'],
  ['FamilySync errors with Ancestry', 'fix-familysync-errors'],
  ['Incorrect parents or duplicate people', 'manage-relationships-ftm-2019'],
  ['Making a printed family history book', 'family-book-creator-ftm-2019'],
];

const FAQS = [
  {
    q: 'Can I keep using FTM 2019?',
    a: 'Yes. Many people still use it every day and we support it fully. If you move to a new operating system or want the latest publisher fixes, FTM 2024 is worth considering.',
  },
  {
    q: 'How do I make sure FTM 2019 is up to date?',
    a: 'Open the Help menu and look for the update option. Always back up your tree first, just in case.',
  },
  {
    q: 'Does FTM 2019 work on Windows 11?',
    a: 'Plenty of people run it on Windows 11 once the final 2019 update is installed. If it misbehaves, install that update first and then get in touch.',
  },
  {
    q: 'Can I switch from a Windows PC to a Mac?',
    a: 'Yes. Back up the tree with media on the PC, copy the backup across, and restore it in Family Tree Maker on the Mac. Run Find Missing Media if any pictures do not appear.',
  },
  {
    q: 'What stops an FTM 2019 tree from becoming corrupted?',
    a: 'Close the program properly, let syncs finish, store the tree on the internal drive, compact it now and then, and back up after each session.',
  },
  {
    q: 'My 2019 serial number is not being accepted. Why?',
    a: 'Make sure it is the serial for the 2019 edition, not an older one, and check for extra spaces or mistyped characters. If it still fails, the activation count may need resetting by the publisher, and we can explain how.',
  },
];

export default function Ftm2019Page() {
  return (
    <PageShell>
      <PageTitle
        title="Family Tree Maker 2019"
        lead="Features, fixes, and friendly how-to guides for the Family Tree Maker edition that millions of trees still live in."
        crumbs={[{ label: 'Family Tree Maker 2019' }]}
      />

      <p>
        When Family Tree Maker 2019 arrived, it fixed many of the sync headaches of earlier releases
        and added color coding, photo repair, and cloud storage. It has aged well, and a large share of
        the people who contact us still use it. Whether you are keeping it running or planning your next
        step, this page gathers the essentials in one place.
      </p>

      <CallButton />

      <section className="entry-section">
        <h2>A Quick Introduction to FTM 2019</h2>
        <p>
          FTM 2019 is desktop genealogy software for Windows and Mac. You record people, events,
          sources, and media in a tree file stored on your own computer, then produce charts, reports,
          and books from it. When you want to reach beyond your own records, it connects to Ancestry
          for hints, searches, and FamilySync, and to FamilySearch for the free shared tree.
        </p>
        <p>
          The 2019 release focused on three things: more dependable syncing with Ancestry, better tools
          for working with photographs, and an extra layer of protection through TreeVault.
        </p>
      </section>

      <section className="entry-section">
        <h2>What FTM 2019 Can Do</h2>
        <div className="table-wrap">
          <table className="data-table data-table--left">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">In plain terms</th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map(([name, text]) => (
                <tr key={name}>
                  <td><strong>{name}</strong></td>
                  <td>{text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Three Reasons People Still Love 2019</h2>
        <div className="img-grid">
          {SHOWCASE.map((item) => (
            <div key={item.title} className="img-card">
              <img src={item.img} alt="" width={640} height={427} loading="lazy" />
              <div className="img-card__body">
                <h3>{item.title}</h3>
                <p>
                  {item.text}{' '}
                  {item.link ? <Link to={articlePath(item.link)}>Learn how ›</Link> : null}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="entry-section">
        <h2>Installing or Reinstalling FTM 2019</h2>
        <h3>On a Windows PC</h3>
        <ol>
          <li>Start the FTM 2019 installer from your download or original disc.</li>
          <li>Follow the prompts and type in your serial number when asked.</li>
          <li>Once it opens, visit the Help menu and install the latest update.</li>
        </ol>
        <h3>On a Mac</h3>
        <ol>
          <li>Open the installer and move Family Tree Maker into Applications.</li>
          <li>Launch it and enter your serial number.</li>
          <li>Look for updates from the program menu and install any that appear.</li>
        </ol>
        <p>
          Installer refusing to run on a very new operating system? That is often the moment to{' '}
          <Link to={articlePath('upgrade-to-family-tree-maker-2024')}>move up to FTM 2024</Link>, or
          call us and we will look at the options with you.
        </p>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Solutions for Everyday FTM 2019 Problems</h2>
        <ul className="link-list">
          {COMMON_ISSUES.map(([label, slug]) => (
            <li key={slug}>
              <Link to={articlePath(slug)}>{label}</Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="entry-section">
        <h2>Warning Signs Your Tree Needs Attention</h2>
        <ul>
          <li>The program takes noticeably longer to open than it used to.</li>
          <li>Searches or switching between people feel sluggish.</li>
          <li>Syncs finish with warnings or never seem to complete.</li>
          <li>People appear twice, or relationships look wrong.</li>
          <li>Photos you know are attached show a broken-link icon.</li>
        </ul>
        <p>
          Any of these is a cue to make a fresh backup and then run Compact File. If things do not
          improve, our <Link to={articlePath('troubleshooting-family-tree-maker-2019')}>FTM 2019 troubleshooting guide</Link>{' '}
          walks through the next steps.
        </p>
      </section>

      <section className="entry-section">
        <h2>Simple Routines That Keep FTM 2019 Healthy</h2>
        <ul>
          <li><strong>Back up regularly:</strong> at the end of every research session, with media included.</li>
          <li><strong>Compact once a month:</strong> the Compact File option trims and tidies large trees.</li>
          <li><strong>Treat syncing gently:</strong> let each sync finish and avoid editing the same person online and offline.</li>
          <li><strong>Stay updated:</strong> keep both FTM 2019 and your operating system current.</li>
          <li><strong>Keep media organized:</strong> one master folder, and no renaming files outside the program.</li>
        </ul>
      </section>

      <HelpBox
        title="Want Us to Look After Your FTM 2019?"
        text="From crashes to missing photos, we fix 2019 problems every day. Call or chat and we will get you back to researching."
      />

      <section className="entry-section">
        <h2>Should You Stay on 2019 or Upgrade?</h2>
        <p>
          If your copy is fully updated and doing everything you need, staying put is perfectly
          reasonable. Upgrading makes more sense when you change computers, move to a newer operating
          system, or want the fixes the publisher is still releasing. Your trees, photos, and sync links
          come with you. Read{' '}
          <Link to={articlePath('upgrade-to-family-tree-maker-2024')}>our upgrade walkthrough</Link> and{' '}
          <Link to={articlePath('ftm-2024-vs-previous-versions')}>our edition comparison</Link> to decide.
        </p>
      </section>

      <section className="entry-section">
        <h2>Our FTM 2019 Guides</h2>
        <ArticleList articles={articlesIn('ftm-2019')} />
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>FTM 2019 Questions and Answers</h2>
        <FaqAccordion items={FAQS} />
      </section>

      <CallButton />
    </PageShell>
  );
}
